import { resolveLocalizedText } from './localizedText';
import type { AttributeDefinition } from '../model/types';
import type {
  CompositeFilterNode,
  CompositeFilterOp,
  PublicationCatalogRule,
  PublicationCriteriaKind,
} from '../model/publicationTypes';

/** Loosely-typed to match react-i18next's `t` without dragging in the full `TFunction` generics (mirrors `ProductPublicationCard`). */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

// ── Building a criteriaPayload from structured UI state ──────────────────────

export function buildMasterCategoryCriteria(categoryIds: string[]): { categoryIds: string[] } {
  return { categoryIds };
}

/**
 * `attributeName` carries the attribute's stable `code` (see `AttributeValueCriteria`'s doc
 * comment in `model/publicationTypes.ts` for why).
 */
export function buildAttributeValueCriteria(
  attributeCode: string,
  values: string[],
): { attributeName: string; values: string[] } {
  return { attributeName: attributeCode, values };
}

export function buildManualProductCriteria(productIds: string[]): { productIds: string[] } {
  return { productIds };
}

// ── Reading a criteriaPayload back into structured UI state (defensive — the wire value is
//    `unknown` at runtime no matter what the TS type claims) ─────────────────────────────────

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

export function readMasterCategoryCriteria(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  return asStringArray((payload as Record<string, unknown>).categoryIds);
}

export function readAttributeValueCriteria(payload: unknown): { attributeCode: string; values: string[] } {
  if (!payload || typeof payload !== 'object') return { attributeCode: '', values: [] };
  const record = payload as Record<string, unknown>;
  const attributeCode = typeof record.attributeName === 'string' ? record.attributeName : '';
  return { attributeCode, values: asStringArray(record.values) };
}

export function readManualProductCriteria(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  return asStringArray((payload as Record<string, unknown>).productIds);
}

// ── Raw JSON editor (COMPOSITE_FILTER) ────────────────────────────────────────

export type ParseCompositeFilterResult =
  | { ok: true; value: CompositeFilterNode }
  | { ok: false; error: string };

const VALID_OPS: CompositeFilterOp[] = ['AND', 'OR', 'NOT'];

/**
 * Best-effort structural validation of a raw-JSON `COMPOSITE_FILTER` payload — parses the JSON and
 * checks the shape the endpoint documents (`{op, children}`, `NOT` takes exactly one child),
 * recursing into nested composite children. Leaf children (category/attribute/product shape) are
 * NOT deeply validated here — that is the backend's job at write time (see the endpoint's own
 * "structurally validated per criteriaKind" description); this is a friendly pre-check, not the
 * authoritative validator.
 */
export function parseCompositeFilterJson(raw: string): ParseCompositeFilterResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'invalidJson' };
  }
  const error = validateCompositeNodeShape(parsed);
  if (error) return { ok: false, error };
  return { ok: true, value: parsed as CompositeFilterNode };
}

function validateCompositeNodeShape(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'notAnObject';
  const record = value as Record<string, unknown>;
  if (typeof record.op !== 'string' || !VALID_OPS.includes(record.op as CompositeFilterOp)) {
    return 'invalidOp';
  }
  if (!Array.isArray(record.children) || record.children.length === 0) {
    return 'missingChildren';
  }
  if (record.op === 'NOT' && record.children.length !== 1) {
    return 'notRequiresOneChild';
  }
  for (const child of record.children) {
    // A child is either a recognizable leaf shape or another composite node — recurse only for
    // the latter; leaves are intentionally not deep-validated (see the doc comment above).
    if (child && typeof child === 'object' && 'op' in (child as Record<string, unknown>)) {
      const nestedError = validateCompositeNodeShape(child);
      if (nestedError) return nestedError;
    }
  }
  return null;
}

// ── Human-readable summaries (Rules list + COMPOSITE_FILTER structured summary) ──────────────

export interface CriteriaDescribeContext {
  categoryNameById: Map<string, string>;
  attributeDictionary: AttributeDefinition[];
  locale: string;
}

function describeAttributeValueLabel(
  ctx: CriteriaDescribeContext,
  attributeCode: string,
  rawValue: string,
): string {
  const def = ctx.attributeDictionary.find((d) => d.code === attributeCode);
  if (def?.type === 'SELECT' || def?.type === 'MULTISELECT') {
    const option = def.options?.find((o) => o.code === rawValue);
    if (option) return resolveLocalizedText(option.translations, 'label', ctx.locale, rawValue);
  }
  if (def?.type === 'BOOLEAN') {
    return rawValue === 'true' ? 'true' : 'false';
  }
  return rawValue;
}

function attributeLabel(ctx: CriteriaDescribeContext, code: string): string {
  const def = ctx.attributeDictionary.find((d) => d.code === code);
  return def ? resolveLocalizedText(def.translations, 'label', ctx.locale, def.code) : code;
}

/** Structural shape-sniffing for a composite child — see `CompositeFilterNode`'s doc comment. */
function describeCompositeChild(ctx: CriteriaDescribeContext, child: unknown, t: Translate): string {
  if (!child || typeof child !== 'object') return t('publicationRulesPanel.criteria.unrecognized');
  const record = child as Record<string, unknown>;
  if (typeof record.op === 'string' && Array.isArray(record.children)) {
    return describeCompositeNode(ctx, record as unknown as CompositeFilterNode, t);
  }
  if (Array.isArray(record.categoryIds)) {
    return describeMasterCategory(ctx, asStringArray(record.categoryIds), t);
  }
  if (typeof record.attributeName === 'string' && Array.isArray(record.values)) {
    return describeAttributeValue(ctx, record.attributeName, asStringArray(record.values), t);
  }
  if (Array.isArray(record.productIds)) {
    return describeManualProduct(asStringArray(record.productIds), t);
  }
  return t('publicationRulesPanel.criteria.unrecognized');
}

function describeMasterCategory(ctx: CriteriaDescribeContext, categoryIds: string[], t: Translate): string {
  if (categoryIds.length === 0) return t('publicationRulesPanel.criteria.masterCategoryEmpty');
  const names = categoryIds.map((id) => ctx.categoryNameById.get(id) ?? id);
  return t('publicationRulesPanel.criteria.masterCategory', { categories: names.join(', ') });
}

function describeAttributeValue(
  ctx: CriteriaDescribeContext,
  attributeCode: string,
  values: string[],
  t: Translate,
): string {
  const label = attributeLabel(ctx, attributeCode);
  const valueLabels = values.map((v) => describeAttributeValueLabel(ctx, attributeCode, v));
  return t('publicationRulesPanel.criteria.attributeValue', {
    attribute: label,
    values: valueLabels.join(', '),
  });
}

function describeManualProduct(productIds: string[], t: Translate): string {
  return t('publicationRulesPanel.criteria.manualProduct', { count: productIds.length });
}

/** Recursively describes a `{op, children}` composite node — used for both the Rules list and the raw-JSON editor's read-only structured summary. */
export function describeCompositeNode(
  ctx: CriteriaDescribeContext,
  node: CompositeFilterNode,
  t: Translate,
): string {
  const opLabel = t(`publicationRulesPanel.criteria.op.${node.op}`);
  const parts = (node.children ?? []).map((child) => describeCompositeChild(ctx, child, t));
  return t('publicationRulesPanel.criteria.composite', { op: opLabel, parts: parts.join(` ${opLabel} `) });
}

/** One-line, human-readable summary of a rule's criteria for the Rules list table. */
export function describeCriteria(
  ctx: CriteriaDescribeContext,
  criteriaKind: PublicationCriteriaKind,
  criteriaPayload: unknown,
  t: Translate,
): string {
  switch (criteriaKind) {
    case 'MASTER_CATEGORY':
      return describeMasterCategory(ctx, readMasterCategoryCriteria(criteriaPayload), t);
    case 'ATTRIBUTE_VALUE': {
      const { attributeCode, values } = readAttributeValueCriteria(criteriaPayload);
      return describeAttributeValue(ctx, attributeCode, values, t);
    }
    case 'MANUAL_PRODUCT':
      return describeManualProduct(readManualProductCriteria(criteriaPayload), t);
    case 'COMPOSITE_FILTER': {
      if (
        criteriaPayload &&
        typeof criteriaPayload === 'object' &&
        typeof (criteriaPayload as Record<string, unknown>).op === 'string'
      ) {
        return describeCompositeNode(ctx, criteriaPayload as CompositeFilterNode, t);
      }
      return t('publicationRulesPanel.criteria.unrecognized');
    }
    default:
      return t('publicationRulesPanel.criteria.unrecognized');
  }
}

/** `PublicationCatalogRule` narrowed to just the two fields `describeCriteria` needs, for callers that already have the full rule. */
export function describeRuleCriteria(
  ctx: CriteriaDescribeContext,
  rule: Pick<PublicationCatalogRule, 'criteriaKind' | 'criteriaPayload'>,
  t: Translate,
): string {
  return describeCriteria(ctx, rule.criteriaKind, rule.criteriaPayload, t);
}
